import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useState } from 'react';
import CodeAuthField from '../../components/code-auth-input/CodeAuthField';
import i18n from '../../i18n';
import { formatApiErrors, hasError } from '../../utils/formatError';

const STEP = {
    START: 'start',
    CODE: 'code',
    PASSWORD: 'password',
    DONE: 'done'
};

// Telegram's login codes are 5 digits in practice. Used only if the start
// response leaves codeLength out — a flash call, for one, reports a pattern.
const DEFAULT_CODE_LENGTH = 5;

// tg-client holds the pending login in memory for ten minutes. Once it has
// expired, or Telegram has expired the code, retyping cannot help — only a new
// code can.
const NEEDS_NEW_CODE = ['PHONE_CODE_EXPIRED', 'LOGIN_NOT_STARTED'];

/**
 * The machine code at the front of a cabinet error message.
 *
 * The cabinet relays tg-client's errors as `{code: 'API_ERROR', message: 'CODE:
 * text'}`, so the Telegram code survives only as a prefix of the message.
 */
const errorCodeOf = (message) =>
    /^([A-Z][A-Z0-9_]*):/.exec(message || '')?.[1] || null;

const describeAccount = ({ tgUserId, tgUsername }) =>
    tgUsername ? `@${tgUsername} (${tgUserId})` : String(tgUserId ?? '');

/**
 * Logs a Telegram user session in: send a code to the session's phone, type it,
 * then the two-step verification password if the account has one.
 *
 * Every step is a call the cabinet relays to tg-client, which holds the
 * half-finished login. Nothing here is stored — the code and password go
 * straight to the request and are dropped from state once it is answered.
 *
 * `closeDialog(changed)` is told whether any call went through, since each one
 * moves the session's status and the grid behind the dialog is then stale.
 */
export const TgSessionLoginDialog = ({ session, openDialog, closeDialog, server }) => {
    const [step, setStep] = useState(STEP.START);
    const [codeLength, setCodeLength] = useState(DEFAULT_CODE_LENGTH);
    const [codeType, setCodeType] = useState(null);
    const [hint, setHint] = useState(null);
    const [password, setPassword] = useState('');
    const [account, setAccount] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [codeReset, setCodeReset] = useState(0);
    const [changed, setChanged] = useState(false);

    const phone = session?.phone || '';

    /** Runs one relayed call. Resolves to its data, or null after showing the error. */
    const call = async (request) => {
        setLoading(true);
        setError(null);
        try {
            const result = await request();
            if (hasError(result)) {
                const message = formatApiErrors(result).join('\n');
                setError({ message, code: errorCodeOf(message) });
                return null;
            }
            setChanged(true);
            return result.data || {};
        } catch (e) {
            console.error('Telegram login error:', e);
            setError({ message: i18n.t('text.unexpectedError'), code: null });
            return null;
        } finally {
            setLoading(false);
        }
    };

    const finish = (data) => {
        if (data.status !== 'active') {
            setError({
                message: `${i18n.t('text.unexpectedError')} (${data.status})`,
                code: null
            });
            return;
        }
        setAccount({ tgUserId: data.tgUserId, tgUsername: data.tgUsername });
        setStep(STEP.DONE);
    };

    const backToStart = () => {
        setError(null);
        setPassword('');
        setStep(STEP.START);
    };

    const sendCode = async () => {
        const data = await call(() => server.loginStart(session.id));
        if (!data) return;
        const length = Number(data.codeLength);
        setCodeLength(Number.isInteger(length) && length > 0 ? length : DEFAULT_CODE_LENGTH);
        setCodeType(data.codeType || null);
        setCodeReset((n) => n + 1);
        setStep(STEP.CODE);
    };

    // CodeAuthField submits by itself once the last box is filled, and clears
    // its boxes as it does.
    const submitCode = async (code) => {
        const data = await call(() => server.loginCode(session.id, code));
        if (!data) {
            // PHONE_CODE_INVALID keeps the pending login on tg-client's side, so
            // the operator simply types it again; refocus the first box for that.
            setCodeReset((n) => n + 1);
            return;
        }
        if (data.status === 'awaiting_password') {
            setHint(data.hint || null);
            setPassword('');
            setStep(STEP.PASSWORD);
            return;
        }
        finish(data);
    };

    const submitPassword = async () => {
        if (!password || loading) return;
        const data = await call(() => server.loginPassword(session.id, password));
        if (!data) return;
        setPassword('');
        finish(data);
    };

    // Not while a call is in flight: its answer is what says whether the
    // session moved.
    const close = () => {
        if (loading) return;
        closeDialog(changed);
    };

    const needsNewCode = NEEDS_NEW_CODE.includes(error?.code);

    return (
        <Dialog open={Boolean(openDialog)} onClose={close} fullWidth maxWidth="xs">
            <DialogTitle>
                {i18n.t('dialogs.tgSessionLogin')}
                {session?.name ? ` — ${session.name}` : ''}
            </DialogTitle>

            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    {error && (
                        <Alert
                            severity="error"
                            sx={{ whiteSpace: 'pre-line' }}
                            action={
                                // The code step has its own "send a new code"
                                // button; the password step has no other way back.
                                needsNewCode && step !== STEP.CODE ? (
                                    <Button color="inherit" size="small" onClick={backToStart}>
                                        {i18n.t('buttons.tgSendNewCode')}
                                    </Button>
                                ) : null
                            }
                        >
                            {error.message}
                        </Alert>
                    )}

                    {step === STEP.START && (
                        <Typography variant="body2">
                            {i18n.t('text.tgLoginStartHint', { phone })}
                        </Typography>
                    )}

                    {step === STEP.CODE && (
                        <>
                            <Typography variant="body2">
                                {i18n.t('text.tgLoginCodeSent', { length: codeLength, phone })}
                            </Typography>
                            {codeType === 'app' && (
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {i18n.t('text.tgLoginCodeViaApp')}
                                </Typography>
                            )}
                            <CodeAuthField
                                // Remounted when the length changes, so the box
                                // count and its refs start over together.
                                key={codeLength}
                                length={codeLength}
                                callback={submitCode}
                                reset={codeReset}
                                isLoading={loading}
                            />
                        </>
                    )}

                    {step === STEP.PASSWORD && (
                        <>
                            <Typography variant="body2">
                                {i18n.t('text.tgLoginPasswordPrompt')}
                            </Typography>
                            <TextField
                                type="password"
                                label={i18n.t('columns.tgPassword')}
                                value={password}
                                onChange={(event) => setPassword(event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') submitPassword();
                                }}
                                helperText={
                                    hint ? i18n.t('text.tgLoginPasswordHint', { hint }) : ''
                                }
                                disabled={loading}
                                // Keeps the browser from filling in the operator's
                                // own cabinet password.
                                autoComplete="new-password"
                                autoFocus
                                fullWidth
                            />
                        </>
                    )}

                    {step === STEP.DONE && account && (
                        <Alert severity="success">
                            {i18n.t('text.tgLoggedInAs', { account: describeAccount(account) })}
                        </Alert>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button color="inherit" onClick={close} disabled={loading}>
                    {i18n.t('buttons.close')}
                </Button>

                {step === STEP.START && (
                    <LoadingButton variant="contained" loading={loading} onClick={sendCode}>
                        {i18n.t('buttons.tgSendCode', { phone })}
                    </LoadingButton>
                )}

                {step === STEP.CODE && (
                    // The code goes to the Telegram app first, and only later by
                    // SMS; someone waiting at the wrong device needs a way out.
                    <Button
                        variant={needsNewCode ? 'contained' : 'text'}
                        onClick={backToStart}
                        disabled={loading}
                    >
                        {i18n.t('buttons.tgSendNewCode')}
                    </Button>
                )}

                {step === STEP.PASSWORD && (
                    <LoadingButton
                        variant="contained"
                        loading={loading}
                        disabled={!password}
                        onClick={submitPassword}
                    >
                        {i18n.t('buttons.tgLogIn')}
                    </LoadingButton>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default TgSessionLoginDialog;
